import { reactive } from 'vue';
import { VueRenderer } from '@tiptap/vue-3';
import { store } from '@/store';
import MentionList from '@/components/widgets/MentionList';
import tippy from 'tippy.js';
import getMemberLabel from '@/utilities/getMemberLabel';
import {
  getDefaultCards,
  getDefaultEpics,
  getDefaultPages,
  getDefaultBoards,
  getDefaultProjects,
} from '@/utilities/getDefaultResources';
import { getRectOrDestroy } from './util';
import setMaxHeight from '@/utilities/setMaxHeight';

const filteredMembers = (query) => {
  const members = [...Object.values(store.getters.getTeamMembers)].map((m) => {
    return {
      ...m,
      description: `${m.display_name ?? ''}${m.first_name ?? ''}${m.last_name ?? ''}${
        m.email ?? ''
      }`,
      value: getMemberLabel(m),
    };
  });
  const filtered = members.filter((m) => m.description.toLowerCase().includes(query.toLowerCase()));
  return filtered;
};

const matches = reactive({
  cards: [],
  boards: [],
  projects: [],
  pages: [],
  members: [],
  epics: [],
  query: '',
});

export default {
  items: ({ query }) => {
    if (query && query !== matches.query) {
      store
        .dispatch('fetchSearchResults', {
          query,
          advanceOptions: { grouped: true, completed: true, cancelled: true },
        })
        .then(({ data }) => {
          matches.cards.splice(0, matches.cards.length, ...(data.cards ?? []));
          matches.epics.splice(0, matches.epics.length, ...(data.epics ?? []));
          matches.boards.splice(0, matches.boards.length, ...(data.boards ?? []));
          matches.projects.splice(0, matches.projects.length, ...(data.projects ?? []));
          matches.pages.splice(0, matches.pages.length, ...(data.pages ?? []));
        })
        .catch(() => {
          // error
        });
      matches.members.splice(0, matches.members.length, ...filteredMembers(query));
    } else if (query === '') {
      matches.cards.splice(0, matches.cards.length, ...getDefaultCards());
      matches.epics.splice(0, matches.epics.length, ...getDefaultEpics());
      matches.boards.splice(0, matches.boards.length, ...getDefaultBoards());
      matches.projects.splice(0, matches.projects.length, ...getDefaultProjects());
      matches.pages.splice(0, matches.pages.length, ...getDefaultPages());
      matches.members.splice(0, matches.members.length, ...filteredMembers('').splice(0, 2));
    }

    if (query !== matches.query) {
      matches.query = query;
    }

    return matches;
  },

  render: () => {
    let component, popup;

    return {
      onStart: (props) => {
        component = new VueRenderer(MentionList, {
          props,
          editor: props.editor,
        });

        if (!props.clientRect) {
          return;
        }

        popup = tippy('body', {
          getReferenceClientRect: getRectOrDestroy(props.clientRect, () => popup),
          appendTo: () => document.body,
          content: component.ref.$el,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
          onShow: setMaxHeight,
          maxWidth: '100%',
          role: 'poper',
        });
      },

      onUpdate(props) {
        component.updateProps(props);

        if (!props.clientRect) {
          return;
        }

        popup[0].setProps({
          getReferenceClientRect: getRectOrDestroy(props.clientRect, () => popup),
        });
      },

      onKeyDown(props) {
        if (props.event.key === 'Escape') {
          popup[0].hide();

          return true;
        }

        return component.ref?.onKeyDown(props);
      },

      onExit() {
        popup?.[0]?.destroy?.();
        component?.destroy?.();
      },
    };
  },
};
